import type React from 'react';
import type { BookDefaultStylesV1 } from './bookDefaults';

export type BookDefaultStylesFormValues = {
  pagePaddingPx?: number;
  baseFontSizePx?: number;
  gridGapPx?: number;
  contentLineGapPx?: number;
  contentAlign?: 'left' | 'center' | 'right' | 'justify';
  defaultTextColor?: string;
  defaultBackgroundColor?: string;
  defaultLinkColor?: string;
  defaultHeaderStyle?: any;
  defaultLineStyle?: any;
  defaultNestedLineStyle?: any;
  defaultDeepNestedLineStyle?: any;
  defaultLinkStyle?: any;
};

export function asPreviewCss(s: any): React.CSSProperties {
  if (!s || typeof s !== 'object') return {};
  const textAlign = s.align || s.textAlign;
  const borderWidth = Number.isFinite(Number(s.borderWidth)) ? Number(s.borderWidth) : 0;
  const borderStyle = s.borderStyle;
  const borderColor = typeof s.borderColor === 'string' ? s.borderColor : undefined;
  const border =
    borderStyle && borderStyle !== 'none' && borderWidth > 0
      ? `${borderWidth}px ${borderStyle} ${borderColor || (typeof s.color === 'string' ? s.color : '#d9d9d9')}`
      : undefined;
  const padding =
    s.paddingLeft || s.paddingRight || s.paddingTop || s.paddingBottom
      ? `${Number(s.paddingTop || 0)}px ${Number(s.paddingRight || 0)}px ${Number(s.paddingBottom || 0)}px ${Number(s.paddingLeft || 0)}px`
      : Number.isFinite(Number(s.padding)) && Number(s.padding) > 0
        ? Number(s.padding)
        : undefined;
  const margin =
    s.marginLeft || s.marginRight || s.marginTop || s.marginBottom
      ? `${Number(s.marginTop || 0)}px ${Number(s.marginRight || 0)}px ${Number(s.marginBottom || 0)}px ${Number(s.marginLeft || 0)}px`
      : Number.isFinite(Number(s.margin)) && Number(s.margin) > 0
        ? Number(s.margin)
        : undefined;
  return {
    fontSize: Number.isFinite(Number(s.fontSize)) ? Number(s.fontSize) : undefined,
    color: typeof s.color === 'string' ? s.color : undefined,
    backgroundColor: typeof s.backgroundColor === 'string' ? s.backgroundColor : undefined,
    fontFamily: typeof s.fontFamily === 'string' ? s.fontFamily : undefined,
    fontWeight: s.bold ? 700 : Number.isFinite(Number(s.fontWeight)) ? Number(s.fontWeight) : undefined,
    fontStyle: s.italic ? 'italic' : undefined,
    textDecoration: s.underline ? 'underline' : undefined,
    textAlign: textAlign === 'left' || textAlign === 'center' || textAlign === 'right' || textAlign === 'justify' ? textAlign : undefined,
    border,
    borderRadius: Number.isFinite(Number(s.borderRadius)) ? Number(s.borderRadius) : undefined,
    padding,
    margin,
    display: 'inline-block',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
  };
}

export function bookDefaultsToFormValues(cur: BookDefaultStylesV1): BookDefaultStylesFormValues {
  return {
    pagePaddingPx: cur.pagePaddingPx,
    baseFontSizePx: cur.baseFontSizePx,
    gridGapPx: cur.gridGapPx,
    contentLineGapPx: cur.contentLineGapPx ?? 0,
    contentAlign: cur.contentAlign ?? 'left',
    defaultTextColor: cur.defaultTextColor || '',
    defaultBackgroundColor: cur.defaultBackgroundColor || '',
    defaultLinkColor: cur.defaultLinkColor || '',
    defaultHeaderStyle: {
      ...(cur.defaultHeaderStyle || {}),
      italic: (cur.defaultHeaderStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultHeaderStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultHeaderStyle as any)?.textAlign || 'left',
    },
    defaultLineStyle: {
      ...(cur.defaultLineStyle || {}),
      bold: (((cur.defaultLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultLineStyle as any)?.textAlign || 'left',
    },
    defaultNestedLineStyle: {
      ...(cur.defaultNestedLineStyle || {}),
      bold: (((cur.defaultNestedLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultNestedLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultNestedLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultNestedLineStyle as any)?.textAlign || 'left',
    },
    defaultDeepNestedLineStyle: {
      ...(cur.defaultDeepNestedLineStyle || {}),
      bold: (((cur.defaultDeepNestedLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultDeepNestedLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultDeepNestedLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultDeepNestedLineStyle as any)?.textAlign || 'left',
    },
    defaultLinkStyle: {
      ...(cur.defaultLinkStyle || {}),
      align: (cur.defaultLinkStyle as any)?.textAlign || 'left',
    },
  };
}

export function formValuesToBookDefaults(vals: any, base: BookDefaultStylesV1): BookDefaultStylesV1 {
  const cleanStringOrUndef = (v: any) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const cleanNumberOrUndef = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
  const cleanPositiveOrUndef = (v: any) => {
    const n = cleanNumberOrUndef(v);
    return n != null && n > 0 ? n : undefined;
  };
  const cleanStyle = (s: any, mode: 'header' | 'line') => {
    if (!s || typeof s !== 'object') return undefined;
    const textAlign = s.align || s.textAlign;
    const span = s.span === 12 || s.span === 24 ? s.span : undefined;
    const baseStyle: any = {
      fontSize: cleanPositiveOrUndef(s.fontSize),
      color: cleanStringOrUndef(s.color),
      backgroundColor: cleanStringOrUndef(s.backgroundColor),
      fontFamily: cleanStringOrUndef(s.fontFamily),
      fontWeight: mode === 'line' ? (s.bold ? 700 : 400) : (cleanNumberOrUndef(s.fontWeight) ?? undefined),
      fontStyle: s.italic ? ('italic' as const) : undefined,
      textDecoration: s.underline ? 'underline' : undefined,
      textAlign: textAlign === 'left' || textAlign === 'center' || textAlign === 'right' || textAlign === 'justify' ? textAlign : undefined,
      borderWidth: cleanPositiveOrUndef(s.borderWidth),
      borderStyle:
        s.borderStyle === 'solid' || s.borderStyle === 'dashed' || s.borderStyle === 'dotted' || s.borderStyle === 'double'
          ? s.borderStyle
          : undefined,
      borderColor: cleanStringOrUndef(s.borderColor),
      borderRadius: cleanPositiveOrUndef(s.borderRadius),
      padding: cleanPositiveOrUndef(s.padding),
      paddingLeft: cleanPositiveOrUndef(s.paddingLeft),
      paddingRight: cleanPositiveOrUndef(s.paddingRight),
      paddingTop: cleanPositiveOrUndef(s.paddingTop),
      paddingBottom: cleanPositiveOrUndef(s.paddingBottom),
      margin: cleanPositiveOrUndef(s.margin),
      marginLeft: cleanPositiveOrUndef(s.marginLeft),
      marginRight: cleanPositiveOrUndef(s.marginRight),
      marginTop: cleanPositiveOrUndef(s.marginTop),
      marginBottom: cleanPositiveOrUndef(s.marginBottom),
    };
    if (mode === 'line' && span) baseStyle.span = span;
    return baseStyle;
  };

  const numOr = (v: any, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const strOr = (v: any, fallback: string) => (typeof v === 'string' ? v : fallback);

  return {
    version: 1 as const,
    pagePaddingPx: numOr(vals.pagePaddingPx, base.pagePaddingPx),
    baseFontSizePx: numOr(vals.baseFontSizePx, base.baseFontSizePx),
    gridGapPx: numOr(vals.gridGapPx, base.gridGapPx),
    contentLineGapPx: Math.max(0, numOr(vals.contentLineGapPx, base.contentLineGapPx ?? 0)),
    contentAlign:
      vals.contentAlign === 'center' ||
      vals.contentAlign === 'right' ||
      vals.contentAlign === 'justify'
        ? vals.contentAlign
        : (base.contentAlign ?? 'left'),
    // Not editable in UI today; preserve.
    headerBackgroundColor: base.headerBackgroundColor || '',
    headerTextColor: base.headerTextColor || '',
    titleColor: base.titleColor || '',
    defaultTextColor: strOr(vals.defaultTextColor, base.defaultTextColor || ''),
    defaultBackgroundColor: strOr(vals.defaultBackgroundColor, base.defaultBackgroundColor || ''),
    defaultLinkColor: strOr(vals.defaultLinkColor, base.defaultLinkColor || ''),
    defaultHeaderStyle: cleanStyle(vals.defaultHeaderStyle, 'header'),
    defaultLineStyle: cleanStyle(vals.defaultLineStyle, 'line'),
    defaultNestedLineStyle: cleanStyle(vals.defaultNestedLineStyle, 'line'),
    defaultDeepNestedLineStyle: cleanStyle(vals.defaultDeepNestedLineStyle, 'line'),
    // Not editable in UI today; preserve.
    defaultCardHeaderStyle: base.defaultCardHeaderStyle,
    defaultCardTitleStyle: base.defaultCardTitleStyle,
    defaultTagStyle: base.defaultTagStyle,
    defaultLinkStyle: cleanStyle(vals.defaultLinkStyle, 'header'),
    defaultGranthaReferenceStyle: base.defaultGranthaReferenceStyle,
    defaultSankalpaBlockStyle: base.defaultSankalpaBlockStyle,
    defaultBhedStyle: base.defaultBhedStyle,
    gridColumns: vals.gridColumns === 2 ? 2 : (base.gridColumns ?? 1),
  };
}

